/**
 * Deterministic reconciliation of a stored layout against a room.
 *
 * Changing the sauna type or the dimensions must never leave a corrupt
 * intermediate: runs that no longer fit inside the new walls are trimmed (and
 * dropped when nothing usable survives), and the heater is moved to the
 * nearest position that still fits. Pure and idempotent — reconciling an
 * already-reconciled layout returns an equal layout, which is what makes it
 * safe to run on every dimension keystroke.
 */
import { MIN_BENCH_RUN_LENGTH_MM } from "./constants";
import type {
	BenchRun,
	HeaterPlacement,
	HeaterSizeMm,
	InteriorLayout,
	InteriorRoom,
} from "./types";
import {
	heaterExtentsFor,
	roomLengthMm,
	roomWidthMm,
	wallAxisLengthMm,
	wallRunsAlongX,
} from "./types";

function clamp(value: number, min: number, max: number): number {
	if (max < min) return min;
	return Math.min(max, Math.max(min, value));
}

/** Room extent perpendicular to a wall — how deep a run on it may reach. */
function wallDepthLimitMm(room: InteriorRoom, wall: BenchRun["wall"]): number {
	return wallRunsAlongX(wall) ? roomLengthMm(room) : roomWidthMm(room);
}

/** Trim one run against ITS OWN room. The caller matches run.room → room. */
function reconcileRun(run: BenchRun, room: InteriorRoom): BenchRun | null {
	const axis = wallAxisLengthMm(room, run.wall);
	if (axis <= 0) return null;

	const startMm = clamp(run.startMm, 0, axis);
	const lengthMm = clamp(run.lengthMm, 0, axis - startMm);
	if (lengthMm < MIN_BENCH_RUN_LENGTH_MM) return null;

	const depthLimit = wallDepthLimitMm(room, run.wall);
	const offsetFromWallMm = clamp(run.offsetFromWallMm, 0, depthLimit);
	const depthMm = clamp(run.depthMm, 0, depthLimit - offsetFromWallMm);
	if (depthMm <= 0) return null;

	if (
		startMm === run.startMm &&
		lengthMm === run.lengthMm &&
		depthMm === run.depthMm &&
		offsetFromWallMm === run.offsetFromWallMm
	) {
		return run;
	}

	return {
		...run,
		startMm,
		lengthMm,
		depthMm,
		offsetFromWallMm,
	};
}

function reconcileHeater(
	heater: HeaterPlacement,
	room: InteriorRoom,
	size: HeaterSizeMm,
): HeaterPlacement {
	const extents = heaterExtentsFor(size, heater.wall);
	const axis = wallAxisLengthMm(room, heater.wall);
	const halfAlong = extents.alongWallMm / 2;
	// The centre must stay far enough from either corner for the unit to fit.
	// On a wall shorter than the heater, `clamp` collapses to the midpoint —
	// the nearest position that exists — rather than throwing.
	const offsetMm = clamp(heater.offsetMm, halfAlong, axis - halfAlong);

	const depthLimit = wallDepthLimitMm(room, heater.wall);
	const wallGapMm = clamp(
		heater.wallGapMm,
		0,
		Math.max(0, depthLimit - extents.fromWallMm),
	);

	return offsetMm === heater.offsetMm && wallGapMm === heater.wallGapMm
		? heater
		: { ...heater, offsetMm, wallGapMm };
}

/**
 * Trim a layout into the rooms it describes.
 *
 * Pass every room the design has — a two-room sauna has two, and each run is
 * measured against its OWN room's walls. A run whose room is not in the list is
 * passed through untouched rather than measured against someone else's walls
 * (see `reconcileRun`).
 *
 * `heaterSize` are the heater's measured extents (see `HeaterSizeMm`), so
 * the unit is kept fully inside the walls rather than clamped by its centre
 * point alone. The heater belongs to the hot room; if no `sauna` room is given,
 * the first one stands in.
 */
export function reconcileInteriorLayout(
	layout: InteriorLayout,
	rooms: InteriorRoom | readonly InteriorRoom[],
	heaterSize: HeaterSizeMm,
): InteriorLayout {
	const roomList = Array.isArray(rooms)
		? (rooms as readonly InteriorRoom[])
		: [rooms as InteriorRoom];
	const byId = new Map(roomList.map((room) => [room.id, room]));

	const benchRuns: BenchRun[] = [];
	let runsChanged = false;
	for (const run of layout.benchRuns) {
		const room = byId.get(run.room);
		if (!room) {
			// No measurements for this run's room — keep it verbatim.
			benchRuns.push(run);
			continue;
		}
		const reconciled = reconcileRun(run, room);
		if (!reconciled) {
			runsChanged = true;
			continue;
		}
		if (reconciled !== run) runsChanged = true;
		benchRuns.push(reconciled);
	}

	const heaterRoom = byId.get("sauna") ?? roomList[0];
	const heater = heaterRoom
		? reconcileHeater(layout.heater, heaterRoom, heaterSize)
		: layout.heater;
	if (!runsChanged && heater === layout.heater) return layout;

	return { ...layout, benchRuns, heater };
}
